import {
  createApi,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';
import apiEndPoints from '../../apiconstants';
import { RootState } from '../../store';
import { getAgenticAnalystBaseUrl, handleErrorResponse } from '../api';

export type AskAgenticAnalyticsRequest = {
  question: string;
  /** Sent as `skills` for decision analysts, `skill` for all others. */
  skills: string | string[];
  verbose: boolean;
  use_cache?: boolean;
};

export type AskAgenticAnalyticsArgs = AskAgenticAnalyticsRequest & {
  isDecision?: boolean;
};

export type AgenticAnalyticsResponseType =
  | 'chart'
  | 'metric'
  | 'grid'
  | 'markdown';

export type AgenticAnalyticsPayload = {
  query?: string;
  title?: string;
  narrative?: string;
  markdown?: string;
  sql?: string;
  drilldowns?: string[];
  data?: Record<string, unknown>[];
  config?: Record<string, unknown>;
  [key: string]: unknown;
};

export type AskAgenticAnalyticsResponse = {
  type?: AgenticAnalyticsResponseType;
  payload?: AgenticAnalyticsPayload;
  block?: {
    type?: AgenticAnalyticsResponseType;
    payload?: AgenticAnalyticsPayload;
  };
  [key: string]: unknown;
};

export const agenticAnalystApiSlice = createApi({
  reducerPath: 'agenticAnalystApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState)?.auth?.token;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    askAgenticAnalytics: builder.mutation<AskAgenticAnalyticsResponse, AskAgenticAnalyticsArgs>({
      query: ({ isDecision, skills, ...payload }) => ({
        url: `${getAgenticAnalystBaseUrl()}${isDecision ? apiEndPoints.askDecisionAnalytics : apiEndPoints.askAgenticAnalytics}`,
        method: 'POST',
        body: isDecision ? { ...payload, skills } : { ...payload, skill: skills },
      }),
      transformResponse: (response: any) => (response?.data ?? response) as AskAgenticAnalyticsResponse,
      transformErrorResponse: handleErrorResponse,
    }),
    getAnalystSkills: builder.query<string[], void>({
      query: () => `${getAgenticAnalystBaseUrl()}${apiEndPoints.getAnalystSkills}`,
      transformResponse: (response: any) => (response?.data ?? response) as string[],
      transformErrorResponse: handleErrorResponse,
    }),
  }),
});

export const { useAskAgenticAnalyticsMutation, useGetAnalystSkillsQuery } = agenticAnalystApiSlice;
